import { datadogRum } from "@datadog/browser-rum";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { ConfirmProvider } from "material-ui-confirm";
import { SnackbarProvider } from "notistack";
import { ReactElement, useEffect, useRef } from "react";
import {
  createBrowserRouter,
  LoaderFunction,
  LoaderFunctionArgs,
  matchRoutes,
  Outlet,
  RouteMatch,
  RouteObject,
  RouterProvider,
  useLocation,
} from "react-router-dom";
import { QueryParamProvider } from "use-query-params";
import { ReactRouter6Adapter } from "use-query-params/adapters/react-router-6";

import Redirect from "~frontend/components/Redirect";
import CaptchaProvider from "~frontend/providers/CaptchaProvider";
import { DateLocalizationProvider } from "~frontend/providers/DateLocalizationProvider";
import IntlProvider from "~frontend/providers/IntlProvider";
import LoggedInUserProvider from "~frontend/providers/LoggedInUserProvider";
import NonCacheClearingApolloClientProvider from "~frontend/providers/NonCacheClearingApolloClientProvider";
import { OnBeforeNavigateProvider } from "~frontend/providers/OnBeforeNavigateProvider";
import TenantSlugProvider from "~frontend/providers/TenantSlugProvider";
import theme from "~frontend/theme";

import EmailVerification from "./EmailVerification";
import ErrorPage from "./ErrorPage";
import { Join } from "./Join";
import LoginError from "./LoginError";
import { PublicComplianceRegistration } from "./compliance/register";
import { OrganizationLayout, organizationRoutes } from "./organization";
import { StaffLayout, staffRoutes } from "./staff";
import { SubaccountLayout, subaccountRoutes } from "./subaccounts";
import { UserLayout, userRoutes } from "./user";

const routes = [
  {
    path: "/",
    element: (
      <AllProviders>
        <Outlet />
      </AllProviders>
    ),
    errorElement: (
      <AllProviders>
        <ErrorPage />
      </AllProviders>
    ),
    children: [
      {
        index: true,
        element: (
          <LoggedInUserProvider>
            <Redirect to="/admin" />
          </LoggedInUserProvider>
        ),
      },
      {
        path: "admin",
        element: (
          <LoggedInUserProvider>
            <SubaccountLayout />
          </LoggedInUserProvider>
        ),
        children: subaccountRoutes,
      },
      {
        path: "subaccounts/:subaccountSlug",
        element: (
          <LoggedInUserProvider>
            <SubaccountLayout />
          </LoggedInUserProvider>
        ),
        children: subaccountRoutes,
      },
      {
        path: "user",
        element: (
          <LoggedInUserProvider>
            <UserLayout />
          </LoggedInUserProvider>
        ),
        children: userRoutes,
      },
      {
        path: "organization",
        element: (
          <LoggedInUserProvider>
            <OrganizationLayout />
          </LoggedInUserProvider>
        ),
        children: organizationRoutes,
      },
      {
        path: "staff",
        element: (
          <LoggedInUserProvider>
            <StaffLayout />
          </LoggedInUserProvider>
        ),
        children: staffRoutes,
      },
      {
        path: "join",
        element: <Join />,
      },
      {
        path: "login-error",
        element: <LoginError />,
      },
      {
        path: "verify-email",
        element: <EmailVerification />,
      },
      {
        path: "compliance/register/:registrationType",
        element: <PublicComplianceRegistration />,
      },
    ],
  },
];

// https://docs.datadoghq.com/real_user_monitoring/browser/advanced_configuration/?tab=npm#react-router-instrumentation
function computeViewName(routeMatches: RouteMatch[]): string {
  let viewName = "";
  for (let index = 0; index < routeMatches.length; index++) {
    const routeMatch = routeMatches[index];
    const path = routeMatch.route.path;
    // Skip pathless routes
    if (!path) {
      continue;
    }

    if (path.startsWith("/")) {
      // Handle absolute child route paths
      viewName = path;
    } else {
      // Handle route paths ending with "/"
      viewName += viewName.endsWith("/") ? path : `/${path}`;
    }
  }

  return viewName || "/";
}

function AllProviders({ children }: { children: ReactElement }) {
  const location = useLocation();

  useEffect(() => {
    const routeMatches = matchRoutes(routes, location.pathname);
    const viewName = routeMatches && computeViewName(routeMatches);
    if (viewName) {
      datadogRum.startView({ name: viewName });
    }
  }, [location.pathname]);

  return (
    <IntlProvider>
      <DateLocalizationProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <SnackbarProvider>
            <ConfirmProvider
              defaultOptions={{
                // This auto-focuses the "confirm" button so you can confirm by
                // pressing Enter
                confirmationButtonProps: {
                  autoFocus: true,
                  variant: "contained",
                  color: "primary",
                },
              }}
            >
              <CaptchaProvider>
                <TenantSlugProvider>
                  <QueryParamProvider
                    adapter={ReactRouter6Adapter}
                    options={{ enableBatching: true }}
                  >
                    <NonCacheClearingApolloClientProvider>
                      {children}
                    </NonCacheClearingApolloClientProvider>
                  </QueryParamProvider>
                </TenantSlugProvider>
              </CaptchaProvider>
            </ConfirmProvider>
          </SnackbarProvider>
        </ThemeProvider>
      </DateLocalizationProvider>
    </IntlProvider>
  );
}

// from: https://stackoverflow.com/a/76834942
function attachLoaderRecursively(
  routes: RouteObject[],
  loader: (
    args: LoaderFunctionArgs<unknown>,
    handlerCtx: unknown,
    route: RouteObject
  ) => ReturnType<LoaderFunction>
) {
  for (const route of routes) {
    if (route.children?.length) {
      attachLoaderRecursively(route.children, loader);
    } else if (route.path) {
      route.loader = (args, ctx) => loader(args, ctx, route);
    }
  }
  return routes;
}

export function RouterRoot(): ReactElement {
  const onBeforeNavigateHandlers = useRef(new Set<() => Promise<void>>());
  const currentRoute = useRef<null | RouteObject>(null);

  attachLoaderRecursively(routes, async (_args, _ctx, route) => {
    if (currentRoute.current === route) {
      return null;
    }
    currentRoute.current = route;

    for (const handler of onBeforeNavigateHandlers.current) {
      await handler();
    }

    return null;
  });

  const router = createBrowserRouter(routes);

  return (
    <OnBeforeNavigateProvider handlers={onBeforeNavigateHandlers.current}>
      <RouterProvider router={router} />
    </OnBeforeNavigateProvider>
  );
}
